import React from 'react';
import { Phone } from 'lucide-react';

// ProfileCard component
const ProfileCard = ({ name, role, avatar, status, phoneNumber }) => {
  return (
    <div className="group relative bg-white rounded-lg shadow-sm overflow-hidden flex flex-col h-full min-h-[350px]">
      {/* Status Badge - Absolute positioned top-left */}
      <div className="absolute top-3 left-3 z-10">
        <span className="bg-green-500 text-white text-xs font-medium px-2.5 py-1 rounded-full">
          {status}
        </span>
      </div>
      
      {/* Card Content */}
      <div className="p-6 flex flex-col items-center text-center flex-grow">
        {/* Avatar - Large circular image (w-24 h-24) with border */}
        <div className="mb-4">
          <img 
            src={avatar} 
            alt={name} 
            className="w-24 h-24 rounded-full border-4 border-white shadow-md mx-auto object-cover"
          />
        </div>
        
        {/* Name - Bold, dark slate text */}
        <h3 className="text-lg font-bold text-slate-800 mb-1">{name}</h3>
        {/* Role - Smaller, light gray */}
        <p className="text-sm text-gray-500 mb-4">{role}</p>
        
        {/* Divider - Dashed line separating content from footer button */}
        <div className="w-full border-t border-dashed border-gray-300 my-4 flex-grow"></div>
      </div>
      
      {/* Action Button (Footer) - Full-width button area at the bottom of the card */}
      <div className="bg-white group-hover:bg-blue-500 transition-colors duration-300">
        {/* Contact Button with Phone Icon */}
        <a 
          href={`tel:${phoneNumber}`}
          className="w-full flex items-center justify-center py-3 text-center text-gray-700 group-hover:text-white font-semibold text-sm uppercase tracking-wider transition-colors duration-300"
        >
          <Phone className="w-4 h-4 mr-2" />
          Contact
        </a>
      </div>
    </div>
  );
};

// Mock data for profiles
const mockProfiles = [
  {
    id: 1,
    name: "Alexandra Morgan",
    role: "Senior UI/UX Designer",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=80",
    status: "Available",
    phoneNumber: "+1-555-123-4567"
  },
  {
    id: 2,
    name: "Michael Chen",
    role: "Full Stack Developer",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=80",
    status: "Available",
    phoneNumber: "+1-555-234-5678"
  },
  {
    id: 3,
    name: "Sarah Johnson",
    role: "Product Manager",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=80",
    status: "Available",
    phoneNumber: "+1-555-345-6789"
  },
  {
    id: 4,
    name: "David Rodriguez",
    role: "DevOps Engineer",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=80",
    status: "Available",
    phoneNumber: "+1-555-456-7890"
  },
  {
    id: 5,
    name: "Emma Wilson",
    role: "Marketing Specialist",
    avatar: "https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=80",
    status: "Available",
    phoneNumber: "+1-555-567-8901"
  },
  {
    id: 6,
    name: "James Thompson",
    role: "Data Scientist",
    avatar: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=80",
    status: "Available",
    phoneNumber: "+1-555-678-9012"
  }
];





// Main App Component
const App = () => {
  return (
    <div className="min-h-screen bg-[#F5F7FA] font-sans">
      {/* Header */}
      <header className="bg-white shadow-sm py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo192.png" 
                alt="SAFEtist Farm Logo" 
                className="w-10 h-10 object-contain"
              />
              <h1 className="text-xl md:text-2xl font-bold text-gray-800 text-center">
                เซฟติสท์ฟาร์ม - SAFETist Farm
              </h1>
            </div>
          </div>
        </div>
      </header>
      
      {/* Grid of Profile Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {mockProfiles.map(profile => (
            <ProfileCard
              key={profile.id}
              name={profile.name}
              role={profile.role}
              avatar={profile.avatar}
              status={profile.status}
              phoneNumber={profile.phoneNumber}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default App;